import { useState } from 'react';

import { useConversation } from 'hooks/conversation';
import { useVendorCommand } from 'hooks/useVendorCommand';
import Tile from './Tile';
import { RatingRequestDataType, RatingRequestProps, RatingsType } from './types';

import {
  IconRatingEmojiNegative,
  IconRatingEmojiNeutral,
  IconRatingEmojiPositive,
} from 'components/Icons';
import Typography from 'components/Typography/Typography';
import { VENDOR_COMMANDS } from 'constants/vendor-commands';
import { MESSAGE_ALIGN } from 'routes/Chat/Messages/constants';
import { PlainTextMessage } from 'routes/Chat/Messages/PlainTextMessage';
import { Prompt, SATISFACTION_RATING_OPTIONS, TWILIO_RATING_TYPES } from './constants';
import { Container, CustomerResponse, TextContainer, Tiles } from './styles';
import WithDelayedAnimation from 'routes/Chat/Messages/WithDelayedAnimation/WithDelayedAnimation';
import { isLastMessage, isMessageRead } from 'utils/twilio';
import { htmlId } from 'utils/html';

const ResponseToIcon = {
  [SATISFACTION_RATING_OPTIONS.GREAT]: <IconRatingEmojiPositive />,
  [SATISFACTION_RATING_OPTIONS.AVERAGE]: <IconRatingEmojiNeutral />,
  [SATISFACTION_RATING_OPTIONS.POOR]: <IconRatingEmojiNegative />,
};

/**
 * To force trigger in conversation (see Message Templates section in README for more info):
 * message_template_rating_request_form *
 */
const RatingRequest = (props: RatingRequestProps) => {
  const { twilioMessageIndex } = props;
  const { conversationState } = useConversation();
  const { runVendorCommandAsync } = useVendorCommand();
  const isCompleted = props.form?.isCompleted ?? false;
  let previousResponse = props.form?.info?.rating;
  previousResponse = (previousResponse?.toLocaleLowerCase() as RatingsType) ?? undefined;

  const isLatestMessage = isLastMessage(twilioMessageIndex, conversationState.messages.length);
  const read = isMessageRead(twilioMessageIndex, conversationState);
  const animate = isLatestMessage && !read;

  const [selectedRating, setSelectedRating] = useState<RatingsType | undefined>(
    previousResponse as RatingsType,
  );

  const onSubmitRating = async (ratingsOption: RatingsType) => {
    await runVendorCommandAsync(VENDOR_COMMANDS.CARVANA_CUSTOMER_CHAT_RATING, {
      rating: ratingsOption.toUpperCase(),
      rating_type: props.form.info?.rating_type || TWILIO_RATING_TYPES.AI_SURVEY,
      form_id: props.form.id,
      twilio_message_id: conversationState.messages[props.twilioMessageIndex].sid,
    } as RatingRequestDataType);
    setSelectedRating(ratingsOption);
  };

  const commonAnimationProps = { animate, showIndicator: true };

  return (
    <Container id={htmlId('message-extension-ratingRequest')}>
      {previousResponse ? (
        <PlainTextMessage
          text={<CustomerResponse>{ResponseToIcon[previousResponse]}</CustomerResponse>}
          align={MESSAGE_ALIGN.RIGHT}
        />
      ) : (
        <>
          <TextContainer>
            <WithDelayedAnimation relativeIndex={0} {...commonAnimationProps}>
              <Typography as='label' size='lg'>
                {Prompt}
              </Typography>
            </WithDelayedAnimation>
          </TextContainer>
          <WithDelayedAnimation relativeIndex={1} {...commonAnimationProps}>
            <Tiles>
              {Object.values(SATISFACTION_RATING_OPTIONS).map((ratingsOption: RatingsType) => (
                <Tile
                  key={ratingsOption}
                  onClick={() => onSubmitRating(ratingsOption)}
                  rating={ratingsOption}
                  selected={selectedRating === ratingsOption}
                  disabled={!!selectedRating || isCompleted || !!previousResponse}
                />
              ))}
            </Tiles>
          </WithDelayedAnimation>
        </>
      )}
    </Container>
  );
};

export default RatingRequest;
